import { Component, OnInit, Output, ViewChild } from "@angular/core";
import { getApp } from "../app";
import { Subject, Subscription, interval } from "rxjs";
import { completeMFA } from "../api.service";
import { MFADetails } from "../../../../shared/models/MFA-details";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { REDIRECT_REASON } from "../routing/routing.service";

@Component({
  selector: "MFA-complete",
  templateUrl: "./MFA-complete.component.html",
  styleUrls: ["./MFA-complete.component.scss"],
})
export class MFACompleteComponent implements OnInit {
  @ViewChild("content", { static: true }) content: any;
  @Output() ok = new Subject<any>();

  otpLength = 8;
  otpCode: string[] = new Array(this.otpLength).fill("");
  otpArray: number[] = Array(this.otpLength)
    .fill(0)
    .map((x, i) => i);
  countdownTime = "05:55";
  remainingTime: number = 5 * 60 + 55; // 5:55 minutes in seconds

  private modalRef: NgbModalRef | null = null;
  private _countdownSubscription: Subscription = new Subscription();

  app = getApp((app) => {
    this.app = app;
  });

  constructor(private modalService: NgbModal) {}

  async ngOnInit() {
    // Update favicon
    this.app.environment.setFavicon(document.getElementById("favicon")!);

    // Update title
    this.app.environment.setTitle();

    // Check if there is remaining time saved in sessionStorage
    const savedTime = sessionStorage.getItem("remainingTime");
    const redirectReason = sessionStorage.getItem(REDIRECT_REASON);
    if (redirectReason != null) {
      sessionStorage.removeItem(REDIRECT_REASON);
    }

    if (savedTime) {
      const now = Date.now();
      const savedTimestamp = sessionStorage.getItem("timestamp");
      const timePassed = Math.floor((now - Number(savedTimestamp)) / 1000);

      this.remainingTime = Math.max(0, Number(savedTime) - timePassed);
    }
    this.startCountdown();
    await this.open();
  }

  async open() {
    try {
      this.modalRef = await this.modalService.open(this.content, {
        windowClass: "mediumModal",
        backdrop: "static",
        keyboard: false,
      });
    } catch (err) {
      // NOP
    }
  }

  onKeyDown(event: KeyboardEvent, index: number): void {
    const input = event.target as HTMLInputElement;

    if (event.key === "Backspace") {
      if (!input.value && index > 0) {
        // Move focus to the previous input and clear its value
        const prevInput = document.getElementById(
          `otp-${index - 1}`
        ) as HTMLInputElement;
        prevInput.focus();
        prevInput.value = "";
        this.otpCode[index - 1] = "";
      } else if (input.value) {
        // If there is a value, allow it to be cleared
        input.value = "";
        this.otpCode[index] = "";
      }
    }
  }

  // Used to handle if a paste code occurred
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasteData = event.clipboardData?.getData("text") || "";
    const pasteOtp = pasteData.split("").slice(0, this.otpLength);

    pasteOtp.forEach((char, index) => {
      this.otpCode[index] = char;
    });
  }

  // Checks if the authentification code is correct
  async submitCode() {
    const mfaCode = this.otpCode.join("");
    const userId = localStorage.getItem("userId");

    this.app.hasError = false;
    this.app.errorText = "";

    if (userId == null) {
      this.app.hasError = true;
      this.app.errorText = this.app.text.auth.loginName;
      return;
    }

    const details: MFADetails = {
      userId: userId,
      authorizationCode: mfaCode,
    };

    const result = await completeMFA(details);

    if (result.authenticated) {
      await this.app.auth.handleAuthentication(result);
      // Delete username from localStorage once the authentification is done with success
      localStorage.removeItem("username");
      this.modalRef?.close();
      this.modalRef = null;
      this._countdownSubscription.unsubscribe();
    } else {
      this.app.hasError = true;
      this.app.errorText = this.app.text.auth.MFAError;
    }
  }

  // Start the countdown from 5:55 minutes
  startCountdown() {
    const timer = interval(1000); // Emit every second (1000 ms)
    this._countdownSubscription = timer.subscribe(() => {
      if (this.remainingTime > 0) {
        this.remainingTime--;
        this.updateCountdownDisplay();
        sessionStorage.setItem("remainingTime", this.remainingTime.toString());
        sessionStorage.setItem("timestamp", Date.now().toString());
      } else {
        // Stop the timer when countdown is finished and remove countdown data from sessionStorage
        this._countdownSubscription.unsubscribe();
      }
    });
  }

  // Updates the countdownTime in "MM:SS" format
  async updateCountdownDisplay() {
    const minutes: number = Math.floor(this.remainingTime / 60);
    const seconds: number = this.remainingTime % 60;

    this.countdownTime = `${this.padZero(minutes)}:${this.padZero(seconds)}`;

    // Redirect to the login page when countdown reach 00:00
    if (minutes === 0 && seconds === 0) {
      this.app.errorText = this.app.text.auth.MFASessionExpired;
      this.app.hasError = true;
      this.modalService.dismissAll();
      await this.app.routing.navigate("login");

      setTimeout(() => {
        this.app.state.next({ hasError: false });
      }, 3000);
    }
  }

  // Ensures that numbers less than 10 are prefixed with a 0
  padZero(value: number) {
    return value < 10 ? `0${value}` : value.toString();
  }

  // Remove non-numeric characters from otp-input if it's the case & focus the next input
  onInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/[^0-9]/g, "");

    if (input.value && index < this.otpLength - 1) {
      const nextInput = document.getElementById(
        `otp-${index + 1}`
      ) as HTMLInputElement;
      nextInput.focus();
    }
  }

  ngOnDestroy() {
    // Unsubscribe when the component is destroyed and delete sessionStorage countdown info
    if (this._countdownSubscription) {
      this._countdownSubscription.unsubscribe();
      sessionStorage.removeItem("remainingTime");
      sessionStorage.removeItem("timestamp");
    }
  }
}
